import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RedeemIcon from '@mui/icons-material/Redeem';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useCouponsAdmin } from './useCouponsAdmin';
import { couponsService } from './coupons.service';
import { discountText, appliesLabel, formatDate, getErrorMessage, restrictionsSummary, STATUS_COLORS, STATUS_LABELS, toCsv, downloadCsv } from './coupons.utils';
import CouponFormDrawer from './CouponFormDrawer';
import CouponDetailDrawer from './CouponDetailDrawer';

const SORT_COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'discountType', label: 'Discount' },
];

const ICON_TONES = {
  blue: { background: '#EFF8FF', color: '#175CD3' },
  green: { background: '#F0FDF4', color: '#16A34A' },
  purple: { background: '#FAF5FF', color: '#7E22CE' },
  amber: { background: '#FFFBEB', color: '#B45309' },
};

const toolbarSelectMenuProps = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' },
      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#EFF8FF', color: '#175CD3' },
      '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: '#DCEEFF' },
      '& .MuiMenuItem-root:hover': { backgroundColor: '#F5FAFF' },
    },
  },
};

const noTransformSx = { textTransform: 'none' };

const toolbarSelectSx = {
  fontWeight: 400,
  fontFamily: 'Inter-Medium, sans-serif',
  '& .MuiSelect-select': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' },
};

function StatCard({ label, value, sub, loading, icon: Icon, tone = 'blue' }) {
  const toneColors = ICON_TONES[tone] || ICON_TONES.blue;
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: '12px', padding: '9px 14px', minWidth: 180, flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}
    >
      {Icon && (
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            backgroundColor: toneColors.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 17, color: toneColors.color }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {label}
        </Box>
        {loading ? (
          <Skeleton variant="text" width={50} height={26} sx={{ marginTop: '2px' }} />
        ) : (
          <Box sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25 }}>
            {value}
          </Box>
        )}
        {loading ? (
          <Skeleton variant="text" width={90} height={13} />
        ) : (
          sub && (
            <Box sx={{ fontSize: 10.5, color: 'text.secondary' }}>
              {sub}
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
}

const SKELETON_ROWS = 5;
const SKELETON_COLUMN_WIDTHS = [90, 70, 80, 60, 70, 70, 90];

function CouponsTableSkeleton() {
  return (
    <TableBody>
      {Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {SKELETON_COLUMN_WIDTHS.map((width, colIndex) => (
            <TableCell key={colIndex} align={colIndex === SKELETON_COLUMN_WIDTHS.length - 1 ? 'right' : 'left'}>
              <Skeleton variant="text" width={width} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

export default function CouponsPage() {
  const {
    items, total, page, pageSize, isLoading, error,
    search, setSearch, status, setStatus, discountType, setDiscountType, appliesTo, setAppliesTo,
    sortBy, sortDir, toggleSort, goToPage, resetFilters, refetch,
  } = useCouponsAdmin();

  const [stats, setStats] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [detailCoupon, setDetailCoupon] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isExporting, setIsExporting] = useState(false);

  const loadStats = useCallback(() => {
    couponsService
      .getAdminStats()
      .then((data) => setStats(data))
      .catch(() => setStats({ total: 0, active: 0, totalRedemptions: 0, expiring: 0 }));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const refreshAll = () => {
    refetch();
    loadStats();
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setFormOpen(true);
  };

  const openEdit = (coupon) => {
    setDetailCoupon(null);
    setEditingCoupon(coupon);
    setFormOpen(true);
  };

  const openDetail = (coupon) => {
    couponsService
      .getCouponAdmin(coupon.id)
      .then((full) => setDetailCoupon(full))
      .catch(() => notify('Could not load coupon details.', 'error'));
  };

  const handleSaved = (_saved, mode) => {
    notify(mode === 'created' ? 'Coupon created.' : 'Coupon updated.', 'success');
    refreshAll();
  };

  const handleToggleStatus = (coupon) => {
    couponsService
      .setCouponStatus(coupon.id, { isActive: !coupon.isActive })
      .then(() => {
        notify(`${coupon.code} ${coupon.isActive ? 'paused' : 'activated'}.`, 'success');
        refreshAll();
      })
      .catch((err) => notify(getErrorMessage(err, 'Could not update status.'), 'error'));
  };

  const handleDuplicate = (coupon) => {
    couponsService
      .duplicateCoupon(coupon.id)
      .then((created) => {
        notify(`Duplicated as ${created.code}.`, 'success');
        refreshAll();
      })
      .catch((err) => notify(getErrorMessage(err, 'Could not duplicate coupon.'), 'error'));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    couponsService
      .deleteCouponAdmin(deleteTarget.id)
      .then((result) => {
        notify(
          result.softDeleted
            ? `${deleteTarget.code} has redemption history — deactivated instead of deleted.`
            : `Deleted ${deleteTarget.code}.`,
          'success',
        );
        setDeleteTarget(null);
        refreshAll();
      })
      .catch((err) => notify(getErrorMessage(err, 'Could not delete coupon.'), 'error'))
      .finally(() => setIsDeleting(false));
  };

  const handleExportCsv = () => {
    setIsExporting(true);
    couponsService
      .listCouponsAdmin({
        page: 1,
        pageSize: Math.max(total, 1000),
        search: search || undefined,
        status: status || undefined,
        discountType: discountType || undefined,
        appliesTo: appliesTo || undefined,
        sortBy,
        sortDir,
      })
      .then((data) => {
        downloadCsv(toCsv(data.items));
        notify('Exported coupons.csv', 'success');
      })
      .catch(() => notify('Could not export coupons.', 'error'))
      .finally(() => setIsExporting(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderSortArrow = (key) =>
    sortBy === key ? (
      sortDir === 'ASC' ? <ArrowUpwardIcon sx={{ fontSize: 13, verticalAlign: 'middle' }} /> : <ArrowDownwardIcon sx={{ fontSize: 13, verticalAlign: 'middle' }} />
    ) : null;

  return (
    <Box sx={{ padding: '18px 24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, fontFamily: 'Inter-Bold, sans-serif' }}>
          Coupon Code Management
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportCsv}
            disabled={isExporting}
            sx={{ ...noTransformSx, fontSize: 12.5 }}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={openCreate}
            sx={{ ...noTransformSx, color: '#fff', fontSize: 12.5 }}
          >
            Create Coupon
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <StatCard icon={ConfirmationNumberIcon} tone="blue" label="Total Coupons" value={stats?.total} sub="in the system" loading={!stats} />
        <StatCard icon={CheckCircleOutlineIcon} tone="green" label="Active" value={stats?.active} sub="redeemable now" loading={!stats} />
        <StatCard icon={RedeemIcon} tone="purple" label="Total Redemptions" value={stats?.totalRedemptions.toLocaleString()} sub="all-time uses" loading={!stats} />
        <StatCard icon={HourglassBottomIcon} tone="amber" label="Expiring ≤ 14 days" value={stats?.expiring} sub="needs attention" loading={!stats} />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by code or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-input': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' } }}
          />
          <Select
            size="small"
            displayEmpty
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 150, ...toolbarSelectSx }}
            MenuProps={toolbarSelectMenuProps}
          >
            <MenuItem value="" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>All statuses</MenuItem>
            <MenuItem value="active" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Active</MenuItem>
            <MenuItem value="scheduled" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Scheduled</MenuItem>
            <MenuItem value="paused" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Paused</MenuItem>
            <MenuItem value="expired" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Expired</MenuItem>
            <MenuItem value="exhausted" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Exhausted</MenuItem>
          </Select>
          <Select
            size="small"
            displayEmpty
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value)}
            sx={{ minWidth: 140, ...toolbarSelectSx }}
            MenuProps={toolbarSelectMenuProps}
          >
            <MenuItem value="" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>All types</MenuItem>
            <MenuItem value="percentage" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Percentage</MenuItem>
            <MenuItem value="flat" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Flat</MenuItem>
          </Select>
          <Select
            size="small"
            displayEmpty
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
            sx={{ minWidth: 160, ...toolbarSelectSx }}
            MenuProps={toolbarSelectMenuProps}
          >
            <MenuItem value="" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>All applies-to</MenuItem>
            <MenuItem value="processing_fee" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Processing fee</MenuItem>
            <MenuItem value="emi" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>EMI</MenuItem>
            <MenuItem value="foreclosure" sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' }}>Foreclosure</MenuItem>
          </Select>
          <Button
            size="small"
            variant="outlined"
            onClick={resetFilters}
            sx={{ ...noTransformSx, borderColor: '#e4e8f0', color: '#1f2733', '&:hover': { borderColor: '#c8cedb', backgroundColor: '#f5f6f8' } }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ marginBottom: '12px' }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
        <TableContainer>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': { fontSize: 12.5, padding: '8px 12px' },
              '& .MuiTableCell-head': { padding: '14px 12px', fontSize: 12.5 },
            }}
          >
            <TableHead sx={{ backgroundColor: '#fafbfd' }}>
              <TableRow>
                {SORT_COLUMNS.map((col) => (
                  <TableCell key={col.key} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700 }} onClick={() => toggleSort(col.key)}>
                    {col.label} {renderSortArrow(col.key)}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700 }}>Restrictions</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700 }} onClick={() => toggleSort('currentUses')}>
                  Usage {renderSortArrow('currentUses')}
                </TableCell>
                <TableCell sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, whiteSpace: 'nowrap' }} onClick={() => toggleSort('validUntil')}>
                  Valid until {renderSortArrow('validUntil')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <CouponsTableSkeleton />
            ) : (
              <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ padding: '40px', color: 'text.secondary' }}>
                    No coupons match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c) => {
                  const pct = c.maxTotalUses > 0 ? Math.min(100, Math.round(((c.currentUses || 0) / c.maxTotalUses) * 100)) : 0;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Box sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12.5, lineHeight: 1.4 }}>{c.code}</Box>
                        {c.description && (
                          <Box sx={{ fontSize: 11, lineHeight: 1.4, color: 'text.secondary' }}>{c.description}</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {discountText(c)}
                        <Box sx={{ fontSize: 11, lineHeight: 1.4, color: 'text.secondary' }}>
                          {appliesLabel(c.appliesTo)}{c.minLoanAmount ? ` · min ₹${c.minLoanAmount}` : ''}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {restrictionsSummary(c).length ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {restrictionsSummary(c).map((r) => (
                              <Chip key={r} size="small" label={r} sx={{ fontSize: 10, height: 20 }} />
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ fontSize: 11, color: 'text.secondary' }}>No restrictions</Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[c.status] || c.status}
                          sx={{ backgroundColor: STATUS_COLORS[c.status]?.bg, color: STATUS_COLORS[c.status]?.color, fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {c.currentUses || 0} / {c.maxTotalUses > 0 ? c.maxTotalUses : '∞'}
                        {c.maxTotalUses > 0 && (
                          <Box sx={{ background: '#eef1f6', borderRadius: '999px', height: 5, width: 70, overflow: 'hidden', marginTop: '4px' }}>
                            <Box sx={{ height: '100%', width: `${pct}%`, background: '#4f46e5' }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.validUntil)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '0px', justifyContent: 'flex-end' }}>
                          <Tooltip title="View">
                            <IconButton size="small" sx={{ padding: '4px' }} onClick={() => openDetail(c)}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          <Tooltip title={c.isActive ? 'Pause' : 'Activate'}>
                            <IconButton size="small" sx={{ padding: '4px' }} onClick={() => handleToggleStatus(c)}>
                              {c.isActive ? <PauseCircleOutlineIcon sx={{ fontSize: 16 }} /> : <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton size="small" sx={{ padding: '4px' }} onClick={() => handleDuplicate(c)}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" sx={{ padding: '4px' }} onClick={() => openEdit(c)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" sx={{ padding: '4px' }} onClick={() => setDeleteTarget(c)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
          <Box sx={{ fontSize: 12.5, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif', color: 'text.secondary' }}>
            Showing {items.length} of {total} coupon(s)
          </Box>
          <Pagination count={totalPages} page={page} onChange={(_e, value) => goToPage(value)} shape="rounded" />
        </Box>
      </Paper>

      <CouponFormDrawer
        open={formOpen}
        coupon={editingCoupon}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <CouponDetailDrawer
        open={!!detailCoupon}
        coupon={detailCoupon}
        onClose={() => setDetailCoupon(null)}
        onEdit={openEdit}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, fontFamily: 'Inter-Bold, sans-serif' }}>
          Delete Coupon
        </DialogTitle>
        <DialogContent sx={{ fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif', lineHeight: 1.5 }}>
          Delete coupon "{deleteTarget?.code}"? If it has redemption history it will be deactivated instead of removed.
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDeleteTarget(null)} disabled={isDeleting} sx={{ ...noTransformSx, fontSize: 12.5 }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
            sx={{ ...noTransformSx, fontSize: 12.5 }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
